import { useEventCreateStore } from '@/src/components/events/useEventCreateStore';
import { MeridiemOptions } from '@/src/constants/DateTimeConstants';
import { getTimezonesWithUTCOffsetAsOptionList } from '@/src/utils/event-time.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import {
  Button,
  Container,
  DatePicker,
  ExpandableSection,
  Form,
  FormField,
  Grid,
  Header,
  Input,
  Select,
  SpaceBetween,
  Textarea,
  TimeInput,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import styles from './AIEventCreateForm.module.scss';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_PROMPT_LENGTH = 1000;

const TIMEZONES = getTimezonesWithUTCOffsetAsOptionList();

const EMPTY_OPTION = { value: '' };

interface AIEventCreateFormProps {
  onClick: () => void;
  audienceTypeOptions: OptionDefinition[];
  privacyTypeOptions: OptionDefinition[];
}

export const AIEventCreateForm: React.FC<AIEventCreateFormProps> = ({
  onClick,
  audienceTypeOptions,
  privacyTypeOptions,
}) => {
  const [numParticipantsValidationError, setNumParticipantsValidationError] = useState('');
  const dataStore = useEventCreateStore();

  const validateNumParticipants = () => {
    let errorType = '';
    if (parseInt(dataStore.numParticipants, 10) < 1) {
      errorType = 'TOO_SMALL';
    }
    if (parseInt(dataStore.numParticipants, 10) > 300) {
      errorType = 'TOO_BIG';
    }
    setNumParticipantsValidationError(errorType);
  };
  const { t } = useTranslation();

  const formIsValid = useMemo(() => {
    if (!!numParticipantsValidationError) return false;
    if (!dataStore?.prompt?.length) return false;
    if (dataStore.prompt.length > MAX_PROMPT_LENGTH) return false;
    if (!dataStore.eventDate) return false;
    return true;
  }, [numParticipantsValidationError, dataStore.prompt, dataStore.eventDate]);

  const reset = () => {
    dataStore.setSelectedDuration('4');
    dataStore.setPrompt('');
  };

  const DURATION_RADIO_GROUP_OPTIONS = ['4', '6', '8'].map((hr) => ({
    label: t(i18nKeys.time.formatted.hours, { amount: hr }),
    value: hr,
  }));

  return (
    <form onSubmit={(e) => e.preventDefault()} className={styles.wrapper}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button formAction="none" onClick={reset} variant="link">
              {t(i18nKeys.general.cancel)}
            </Button>
            <Button variant="primary" disabled={!formIsValid} data-testid="new-ai-event__submit-btn" onClick={onClick}>
              {t(i18nKeys.general.submit)}
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          <Container
            header={
              <Header variant="h2" description={t(i18nKeys.aiEventCreator.planEvent)}>
                {t(i18nKeys.eventTemplates.catalogDetails.planEvent.title)}
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="l">
              <FormField
                label={t(i18nKeys.events.fields.numberOfParticipants.title)}
                errorText={numParticipantsValidationError ? t(i18nKeys.aiEventCreator.numParticipantsInvalid) : null}
              >
                <Input
                  onChange={({ detail }) => dataStore.setNumParticipants(detail.value)}
                  onBlur={() => validateNumParticipants()}
                  inputMode="numeric"
                  type="number"
                  value={dataStore.numParticipants}
                  invalid={!!numParticipantsValidationError?.length}
                  placeholder={t(i18nKeys.aiEventCreator.numberOfParticipantsPlaceholder)}
                  data-testid="new-ai-event__num-participants-input"
                />
              </FormField>
              <FormField
                label={t(i18nKeys.eventTemplates.step5.steps.step1.eventDurationTitle)}
                description={t(i18nKeys.aiEventCreator.eventDurationDescription)}
              >
                <Select
                  onChange={({ detail }) => dataStore.setSelectedDuration(detail.selectedOption.value || '')}
                  selectedOption={
                    DURATION_RADIO_GROUP_OPTIONS.find((d) => d.value === dataStore.selectedDuration) || EMPTY_OPTION
                  }
                  options={DURATION_RADIO_GROUP_OPTIONS}
                  data-testid="new-ai-event__duration-select"
                />
              </FormField>
              <Grid gridDefinition={[{ colspan: { s: 5, default: 12 } }, { colspan: { s: 3, default: 12 } }]}>
                <FormField
                  label={t(i18nKeys.events.fields.audience.title)}
                  description={t(i18nKeys.events.eventDetails.messages.whoIsThisEventIntendedFor)}
                  key="channel"
                >
                  <Select
                    options={audienceTypeOptions}
                    selectedOption={dataStore.audienceType}
                    onChange={({ detail }) => dataStore.setAudienceType(detail.selectedOption)}
                    data-testid="new-ai-event__audience-type"
                  />
                  {dataStore.audienceType &&
                    dataStore.audienceType.value &&
                    (() => {
                      const warningKey = `events.eventDetails.labels.channelWarnings.${dataStore.audienceType.value}`;
                      const warningText = t(warningKey as any);
                      // Only show warning if translation exists (doesn't return the key itself)
                      return warningText && warningText !== warningKey ? (
                        <div className={styles.channelWarning}>
                          <b>{t(i18nKeys.general.warning)}: </b>
                          {warningText}
                        </div>
                      ) : null;
                    })()}
                </FormField>
                <FormField
                  label={t(i18nKeys.events.eventDetails.headers.eventPrivacyType)}
                  description={<>&nbsp;</>}
                  key="privacy-type"
                >
                  <Select
                    options={privacyTypeOptions}
                    selectedOption={dataStore.privacyType}
                    onChange={({ detail }) => dataStore.setPrivacyType(detail.selectedOption)}
                    data-testid="new-ai-event__privacy-type"
                  />
                </FormField>
              </Grid>
              <SpaceBetween direction="horizontal" size="xxl">
                <FormField label={t(i18nKeys.dateRangePicker.startDateLabel)}>
                  <DatePicker
                    value={dataStore.eventDate}
                    onChange={({ detail }) => dataStore.setEventDate(detail.value)}
                    data-testid="new-ai-event__start-date-picker"
                    isDateEnabled={(date: Date) => {
                      return moment(date).unix() > moment().unix();
                    }}
                  />
                </FormField>
                <FormField label={t(i18nKeys.dateRangePicker.startTimeLabel)}>
                  <SpaceBetween direction="horizontal" size="m">
                    <TimeInput
                      value={dataStore.eventTime}
                      data-testid="new-ai-event__start-time-input"
                      onChange={({ detail }) => dataStore.setEventTime(detail.value)}
                      format="hh:mm"
                    />

                    <Select
                      options={[MeridiemOptions.AM, MeridiemOptions.PM]}
                      onChange={({ detail }) => dataStore.setSelectedMeridiem(detail.selectedOption)}
                      selectedOption={dataStore.selectedMeridiem}
                      data-testid="new-ai-event__am-pm-select"
                    />
                  </SpaceBetween>
                </FormField>
              </SpaceBetween>
              <FormField label={t(i18nKeys.aiEventCreator.timezoneLabel)}>
                <Select
                  selectedOption={dataStore.eventTimezone}
                  options={TIMEZONES}
                  onChange={({ detail }) => dataStore.setEventTimezone(detail.selectedOption)}
                  data-testid="new-ai-event__timezone-select"
                />
              </FormField>
            </SpaceBetween>
          </Container>
          <Container
            header={
              <Header variant="h2" description={t(i18nKeys.aiEventCreator.eventObjectiveDescription)}>
                {t(i18nKeys.aiEventCreator.eventObjective)}
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="l">
              <ExpandableSection headerText={t(i18nKeys.aiEventCreator.exampleInput.examplePrompt)}>
                <Container>
                  <SpaceBetween direction="vertical" size="s">
                    <div>
                      <strong>1.</strong> {t(i18nKeys.aiEventCreator.exampleInput.examples.example1)}
                    </div>
                    <div>
                      <strong>2.</strong> {t(i18nKeys.aiEventCreator.exampleInput.examples.example2)}
                    </div>
                    <div>
                      <strong>3.</strong> {t(i18nKeys.aiEventCreator.exampleInput.examples.example3)}
                    </div>
                  </SpaceBetween>
                </Container>
              </ExpandableSection>

              <FormField
                description={<>{t(i18nKeys.general.avoidPIIWarning)}</>}
                constraintText={
                  <>
                    {dataStore.prompt.length}/{MAX_PROMPT_LENGTH}
                  </>
                }
                errorText={dataStore.prompt.length > MAX_PROMPT_LENGTH && t(i18nKeys.aiEventCreator.userPromptTooLong)}
              >
                <Textarea
                  onChange={({ detail }) => dataStore.setPrompt(detail.value)}
                  value={dataStore.prompt}
                  rows={6}
                  invalid={dataStore.prompt.length > MAX_PROMPT_LENGTH}
                  placeholder={t(i18nKeys.aiEventCreator.placeholder)}
                />
              </FormField>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Form>
    </form>
  );
};
